import type { CostPriceAnalysis, CostPriceIngredient } from '../services/costPrice/costPriceService';
import type { Subrecipe } from '../services/subrecipes/subrecipesService';
import type { OrderHistory } from '../stores/appOrderHistoryStore';
import { calculateCurrentPrice, SUBRECIPE_MAX_DEPTH } from './costPriceUtils';
import type { EnrichmentOptions } from './costPriceUtils';

type SubrecipePriceOptions = Pick<EnrichmentOptions, 'customIngredients' | 'showExVat' | 'allSubrecipes'>;

/** Display-only coverage check: canonical totals treat unresolved prices as zero. */
export function hasSubrecipePrices(subrecipe: Subrecipe, orders: OrderHistory[], opts: SubrecipePriceOptions = {}): boolean {
  const visit = (recipe: Subrecipe, path: Set<string>): boolean => {
    if (!Number.isFinite(recipe.portions) || recipe.portions <= 0 || !recipe.ingredients.length
      || path.has(recipe.id) || path.size >= SUBRECIPE_MAX_DEPTH) return false;
    const next = new Set(path).add(recipe.id);
    return recipe.ingredients.every(row => {
      if (row.nestedSubrecipeId) {
        const child = opts.allSubrecipes?.get(row.nestedSubrecipeId);
        return Number.isFinite(row.nestedSubrecipePortionsNeeded) && !!child && visit(child, next);
      }
      if (!Number.isFinite(row.quantityNeeded)) return false;
      const price = calculateCurrentPrice({
        ...row, custom: row.isCustom,
      }, orders, opts.customIngredients, opts.showExVat ?? true).currentPrice;
      return Number.isFinite(price);
    });
  };
  return visit(subrecipe, new Set());
}

/** These checks intentionally leave the established partial BYO-choice policy unchanged. */
export function hasDishPrices(dish: CostPriceAnalysis, orders: OrderHistory[], opts: EnrichmentOptions = {}): boolean {
  return dishPrices(dish, orders, opts, new Set(), 0);
}

export function hasIngredientPrices(row: CostPriceIngredient, orders: OrderHistory[], opts: EnrichmentOptions = {}): boolean {
  return ingredientPrices(row, orders, opts, new Set(), 0);
}

export function hasIngredientCost(row: CostPriceIngredient, orders: OrderHistory[], opts: EnrichmentOptions = {}): boolean {
  return hasQuantity(row) && hasIngredientPrices(row, orders, opts);
}

function hasQuantity(row: CostPriceIngredient): boolean {
  if (row.subrecipeId) return Number.isFinite(row.subrecipePortionsNeeded);
  // Existing nested dishes default an omitted (or zero) amount to one.
  if (row.nestedCostPriceId) return row.nestedQuantityNeeded === undefined || Number.isFinite(row.nestedQuantityNeeded);
  return Number.isFinite(row.quantityNeeded);
}

function dishPrices(dish: CostPriceAnalysis, orders: OrderHistory[], opts: EnrichmentOptions, path: Set<string>, depth: number): boolean {
  if (!dish.ingredients.length || path.has(dish.id) || depth > 3) return false;
  const next = new Set(path).add(dish.id);
  return dish.ingredients.every(row => hasQuantity(row) && ingredientPrices(row, orders, opts, next, depth));
}

function ingredientPrices(row: CostPriceIngredient, orders: OrderHistory[], opts: EnrichmentOptions, path: Set<string>, depth: number): boolean {
  // Nested dishes retain the same pricing context as the canonical enrichment.
  if (!row.subrecipeId && row.nestedCostPriceId) {
    const nested = opts.allCostPriceAnalyses?.find(dish => dish.id === row.nestedCostPriceId);
    return !!nested && dishPrices(nested, orders, opts, path, depth + 1);
  }
  let scopedOrders = orders;
  let scopedCustoms = opts.customIngredients;
  let scopedSubrecipes = opts.allSubrecipes;
  if (row.sourceRestaurantId && row.sourceRestaurantId !== opts.currentRestaurantId) {
    const sourceOrders = opts.byRestaurant?.orders.get(row.sourceRestaurantId);
    if (!sourceOrders) return false;
    scopedOrders = sourceOrders;
    scopedCustoms = opts.byRestaurant?.custom.get(row.sourceRestaurantId);
    scopedSubrecipes = opts.byRestaurant?.subrecipes.get(row.sourceRestaurantId);
  }
  if (row.subrecipeId) {
    const subrecipe = scopedSubrecipes?.get(row.subrecipeId);
    return !!subrecipe && hasSubrecipePrices(subrecipe, scopedOrders, {
      customIngredients: scopedCustoms, allSubrecipes: scopedSubrecipes, showExVat: opts.showExVat,
    });
  }
  return Number.isFinite(calculateCurrentPrice(row, scopedOrders, scopedCustoms, opts.showExVat ?? true).currentPrice);
}
